import { useState } from "react";
import { FileSpreadsheet, FileText, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { useSource } from "@/context/SourceContext";

export const exportReport = async (download, report, params, format) => {
  const qs = new URLSearchParams({ ...params, report, format }).toString();
  const base = report === "full" ? "full_report" : report;
  await download(`/reports/export?${qs}`, `${base}_${params.from}_${params.to}_${params.granularity}.${format}`);
};

export const ExportButtons = ({ report, params, disabled, size = "sm", labelPrefix = "Export" }) => {
  const [busy, setBusy] = useState("");
  const { download, canExport = true } = useSource();
  if (!canExport) return null;
  const run = async (format) => {
    setBusy(format);
    try {
      await exportReport(download, report, params, format);
    } catch (_) {
      toast.error("Export failed. Please try again.");
    } finally {
      setBusy("");
    }
  };
  return (
    <div className="flex gap-2">
      {[["xlsx", "Excel", FileSpreadsheet], ["pdf", "PDF", FileText]].map(([fmt, label, Icon]) => (
        <Button key={fmt} variant="outline" size={size} className="gap-1.5 bg-white" disabled={disabled || !!busy} onClick={() => run(fmt)} data-testid={`export-${report}-${fmt}`}>
          {busy === fmt ? <Loader2 className="h-4 w-4 animate-spin" /> : <Icon className="h-4 w-4" />} {labelPrefix} {label}
        </Button>
      ))}
    </div>
  );
};

export const ReportCard = ({ id, title, description, params, disabled, children, className = "" }) => (
  <section className={`rise-in rounded-lg border border-slate-200 bg-white shadow-sm ${className}`} data-testid={`report-${id}`}>
    <div className="flex flex-wrap items-start justify-between gap-3 border-b border-slate-100 px-6 py-4">
      <div>
        <h2 className="text-base font-semibold text-slate-900 md:text-lg">{title}</h2>
        {description && <p className="mt-0.5 text-xs text-slate-500">{description}</p>}
      </div>
      <ExportButtons report={id} params={params} disabled={disabled} />
    </div>
    <div className="p-6">{children}</div>
  </section>
);

export const EmptyChart = ({ testid }) => (
  <div className="ledger-grid flex h-72 items-center justify-center rounded-md text-sm text-slate-500" data-testid={testid}>No data for this period</div>
);
